import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import CitySwitcher from "./CitySwitcher";
import { useAuth } from "../lib/auth";
import {
  Home,
  Plus,
  ListChecks,
  Trophy,
  Wallet,
  Bell,
  User as UserIcon,
  LogOut,
  Flame,
  ShieldAlert,
} from "lucide-react";

const navItems = [
  { to: "/dashboard", label: "Browse", icon: Home, testid: "nav-browse" },
  { to: "/post", label: "Post Task", icon: Plus, testid: "nav-post" },
  { to: "/my-tasks", label: "My Tasks", icon: ListChecks, testid: "nav-my-tasks" },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, testid: "nav-leaderboard" },
  { to: "/wallet", label: "Wallet", icon: Wallet, testid: "nav-wallet" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top bar */}
      <header
        className="sticky top-0 z-30 bg-[var(--cs-bg)] border-b-2"
        style={{ borderColor: "var(--cs-ink)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/dashboard" className="shrink-0" data-testid="top-logo-link">
            <Logo size={26} />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-3 py-2 rounded-lg font-bold text-sm transition-all ${
                    isActive
                      ? "bg-[var(--cs-ink)] text-white"
                      : "text-[var(--cs-ink)] hover:bg-slate-200"
                  }`
                }
              >
                <item.icon size={16} />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <CitySwitcher />
            {user?.is_admin && (
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `hidden sm:inline-flex items-center gap-1.5 brut-chip ${isActive ? "bg-[var(--cs-flame)] text-white" : ""}`
                }
                title="Admin console"
              >
                <ShieldAlert size={14} /> Admin
              </NavLink>
            )}
            <Link
              to="/notifications"
              className="brut-btn-ghost p-2 rounded-lg hover:bg-slate-200 relative"
              data-testid="nav-notifications"
            >
              <Bell size={20} />
            </Link>
            <Link
              to="/profile"
              className="hidden sm:inline-flex items-center gap-2 brut-chip"
              data-testid="nav-profile"
              title="Profile"
            >
              <Flame size={14} className="text-[var(--cs-flame)]" />
              <span className="tabular">{user?.streak_days ?? 0}</span>
              <span className="text-[var(--cs-ink-2)] hidden lg:inline">·</span>
              <span className="hidden lg:inline">{user?.name?.split(" ")[0]}</span>
            </Link>
            <button
              onClick={handleLogout}
              className="brut-btn-ghost p-2 rounded-lg hover:bg-slate-200"
              data-testid="logout-btn"
              title="Log out"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6 pb-24 md:pb-12">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white bottom-nav"
        style={{ borderTop: "2px solid var(--cs-ink)" }}
      >
        <div className="grid grid-cols-5">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mob-${item.testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-bold uppercase tracking-wide ${
                  isActive ? "text-[var(--cs-money-2)]" : "text-[var(--cs-ink)]"
                }`
              }
            >
              <item.icon size={20} />
              {item.label.split(" ")[0]}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
